import { Check, Plus, Trash2, X } from "lucide-react";
import React from "react";
import { Button } from "../../../../../../components/ui/common/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../../../components/ui/common/tooltip";

interface RecordFormShellProps {
  /** Shown top-left, e.g. `Chemical 1 — chem 1`. */
  title?: string;
  onDelete?: () => void;
  /** Draft (add) mode renders "Save Record" and hides Cancel. */
  isDraft?: boolean;
  onCancel?: () => void;
  submitDisabled?: boolean;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  children: React.ReactNode;
}

/**
 * The `<form>` chrome shared by every `mappingReview` record editor: an optional
 * title/delete header, the caller's fields, and the Cancel / Save footer.
 * Callers own the `useForm` instance and wrap this in `<Form {...form}>`.
 */
const RecordFormShell: React.FC<RecordFormShellProps> = ({
  title,
  onDelete,
  isDraft = false,
  onCancel,
  submitDisabled = false,
  onSubmit,
  children
}) => (
  <form onSubmit={onSubmit} className="tw-space-y-2">
    {(title || onDelete) && (
      <div className="tw-flex tw-items-center tw-justify-between">
        {title && <span className="tw-text-xs tw-font-semibold tw-text-foreground">{title}</span>}
        {onDelete && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="tw-h-7 tw-w-7 tw-text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="tw-h-3 tw-w-3" />
          </Button>
        )}
      </div>
    )}

    {children}

    <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-1">
      {!isDraft && onCancel && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="tw-h-7 tw-text-xs tw-gap-1"
                onClick={onCancel}
              >
                <X className="tw-h-3 tw-w-3" /> Cancel
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="tw-text-xs">Discard changes</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button type="submit" size="sm" className="tw-h-7 tw-text-xs tw-gap-1" disabled={submitDisabled}>
              {isDraft ? (
                <>
                  <Plus className="tw-h-3 tw-w-3" /> Save Record
                </>
              ) : (
                <>
                  <Check className="tw-h-3 tw-w-3" /> Save
                </>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="tw-text-xs">Save changes</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  </form>
);

export default RecordFormShell;
